import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { ArrowRightIcon } from "lucide-react";
import { mockData } from "../data/mock";

const SpecialtiesSection = () => {
  const navigate = useNavigate();

  // Function to get the correct route for each specialty
  const getSpecialtyRoute = (specialtyName) => {
    const routeMap = {
      "Ophthalmology": "/ophthalmology/cataract-surgery",
      "Piles": "/piles",
      "Hernia": "/hernia",
      "Gall Stone": "/gall-stone",
      "Kidney Stone": "/kidney-stone",
      "Circumcision": "/circumcision",
      "Proctology": "/specialty/proctology",
      "Laparoscopy": "/specialty/laparoscopy",
      "Gynaecology": "/specialty/gynaecology",
      "ENT (Ear, Nose, Throat)": "/specialty/ent",
      "Urology": "/specialty/urology",
      "Vascular": "/specialty/vascular",
      "Aesthetics": "/specialty/aesthetics",
      "Orthopedics (Bone and Joint)": "/specialty/orthopedics",
      "Fertility": "/specialty/fertility",
      "Weight Loss": "/specialty/weight-loss",
      "Dermatology": "/specialty/dermatology"
    };
    
    return routeMap[specialtyName] || `/specialty/${specialtyName.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '')}`;
  };

  const handleSpecialtyClick = (specialtyName) => {
    const route = getSpecialtyRoute(specialtyName);
    navigate(route);
  };

  const getSpecialtyIcon = (name) => {
    const icons = {
      "Proctology": "🩺",
      "Laparoscopy": "⚕️", 
      "Gynaecology": "🌸",
      "ENT (Ear, Nose, Throat)": "👂",
      "Urology": "🔬",
      "Vascular": "❤️",
      "Aesthetics": "✨",
      "Orthopedics (Bone and Joint)": "🦴",
      "Ophthalmology": "👁️",
      "Fertility": "🤱",
      "Weight Loss": "⚖️",
      "Dermatology": "🧴",
      "Gall Stone": "🫀",
      "Kidney Stone": "🫘",
      "Piles": "🩹",
      "Circumcision": "🔧",
      "Hernia": "🩺"
    };
    return icons[name] || "🏥";
  };

  const getSpecialtyColor = (index) => {
    const colors = [
      "from-blue-500 to-cyan-600",
      "from-red-500 to-pink-600", 
      "from-pink-500 to-rose-600",
      "from-yellow-500 to-orange-600",
      "from-blue-600 to-indigo-700",
      "from-purple-500 to-indigo-600",
      "from-green-500 to-teal-600",
      "from-orange-500 to-red-600",
      "from-cyan-500 to-blue-600",
      "from-indigo-500 to-purple-600",
      "from-teal-500 to-green-600",
      "from-rose-500 to-pink-600"
    ];
    return colors[index % colors.length];
  };

  return (
    <section className="py-8 sm:py-12 lg:py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Our Specialties
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4 sm:px-0">
            Comprehensive medical care across multiple specialties with experienced doctors and advanced treatments
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {mockData.specialties.map((specialty, index) => (
            <Card 
              key={specialty.name}
              className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-0 bg-white hover:-translate-y-1"
              onClick={() => handleSpecialtyClick(specialty.name)}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  {/* Icon */}
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${getSpecialtyColor(index)} rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0`}>
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white bg-opacity-90 rounded-lg sm:rounded-xl flex items-center justify-center">
                      <span className="text-lg sm:text-2xl">{getSpecialtyIcon(specialty.name)}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {specialty.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 line-clamp-2">
                      {specialty.description}
                    </p>
                    
                    {/* Treatments */}
                    <div className="space-y-0.5 sm:space-y-1 mb-3 sm:mb-4">
                      {specialty.treatments?.slice(0, 3).map((treatment, idx) => (
                        <div key={idx} className="text-xs text-gray-500 flex items-center">
                          <div className="w-1 h-1 bg-blue-400 rounded-full mr-2 flex-shrink-0"></div>
                          <span className="truncate">{treatment}</span>
                        </div>
                      ))}
                    </div>

                    {/* Arrow */}
                    <div className="flex items-center text-blue-600 text-xs sm:text-sm font-medium">
                      <span className="mr-2">Learn More</span>
                      <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-8 sm:mt-10 lg:mt-12">
          <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-lg max-w-4xl mx-auto">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
              Need Help Finding the Right Treatment?
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-4 sm:px-0">
              Our medical experts are here to guide you through your healthcare journey
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base">
                Consult Our Doctors
              </button>
              <button className="border-2 border-gray-600-500 text-orange-500 hover:bg-gray-900 hover:text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base">
                <span className="hidden sm:inline">Call: +91 93117 20045</span>
                <span className="sm:hidden">Call Now</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpecialtiesSection;